import { useState, useEffect } from 'react';
import { IconChevronDownFilled } from '@tabler/icons-react';
import { ensureMinLoading } from '../Utils/withLoading';
import { apiFetch } from '../api/client';

const FONT = '"Public Sans", sans-serif';

const inputClass =
  'w-full outline-none bg-white/[0.08] focus:bg-white/[0.12] focus:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)] placeholder:text-white/30 transition-all duration-200 ease-out';

const inputStyle = {
  border: 'none',
  borderRadius: '16px',
  height: '48px',
  padding: '0 16px',
  fontSize: '15px',
  fontWeight: 500,
  fontFamily: FONT,
  color: 'rgba(255, 255, 255, 0.95)',
  width: '100%',
  boxSizing: 'border-box',
};

const selectStyle = {
  ...inputStyle,
  appearance: 'none',
  WebkitAppearance: 'none',
  cursor: 'pointer',
  paddingRight: '48px',
};

const textareaStyle = {
  ...inputStyle,
  borderRadius: '18px',
  height: 'auto',
  minHeight: '96px',
  padding: '14px 16px',
  resize: 'none',
  lineHeight: '21px',
};

const labelStyle = {
  fontSize: '13px',
  lineHeight: '18px',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.45)',
  fontFamily: FONT,
};

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Efectivo' },
  { value: 'card', label: 'Tarjeta' },
  { value: 'transfer', label: 'Transferencia' },
  { value: 'debit', label: 'Débito' },
  { value: 'credit', label: 'Crédito' },
  { value: 'mixed', label: 'Mixto' },
  { value: 'pending', label: 'Pendiente' },
];

function todayLocal() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  return new Date(d.getTime() - offset * 60000).toISOString().slice(0, 10);
}

function SelectChevron() {
  return (
    <span
      className="flex items-center justify-center pointer-events-none absolute w-6 h-6 rounded-full"
      style={{
        right: '10px',
        top: '50%',
        transform: 'translateY(-50%)',
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
      }}
    >
      <IconChevronDownFilled size={12} color="rgba(255, 255, 255, 0.7)" />
    </span>
  );
}

export default function ModalAgregarGasto({ isOpen, onClose, onCreated, notify }) {
  const [form, setForm] = useState({
    descripcion: '',
    monto: '',
    categoria: '',
    fecha: todayLocal(),
    metodo: 'cash',
    proveedor: '',
    notas: '',
  });
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm({
        descripcion: '',
        monto: '',
        categoria: '',
        fecha: todayLocal(),
        metodo: 'cash',
        proveedor: '',
        notas: '',
      });
      setSaving(false);
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const res = await apiFetch(`/expense-categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch {}
  };

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const montoNum = parseFloat(form.monto);
  const canSave = form.descripcion.trim() && !isNaN(montoNum) && montoNum > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;

    setSaving(true);
    notify?.('loading', 'Registrando gasto...');
    const startedAt = Date.now();

    try {
      const body = {
        description: form.descripcion.trim(),
        amount: montoNum,
        expense_date: form.fecha ? new Date(`${form.fecha}T12:00:00`).toISOString() : null,
        payment_method: form.metodo,
      };

      if (form.categoria) body.category_id = Number(form.categoria);
      if (form.proveedor.trim()) body.supplier_vendor = form.proveedor.trim();
      if (form.notas.trim()) body.notes = form.notas.trim();

      const res = await apiFetch(`/expenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let msg = 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.';
        try {
          const err = await res.json();
          if (err.detail) msg = err.detail;
          else if (Array.isArray(err.detail)) msg = err.detail.map((d) => d.msg).join(', ');
        } catch {}
        notify?.('clear');
        notify?.('error', msg);
        return;
      }

      await ensureMinLoading(startedAt);
      notify?.('clear');
      notify?.('success', 'Gasto registrado correctamente');
      onCreated?.();
      onClose();
    } catch (e) {
      notify?.('clear');
      notify?.('error', 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-3"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={saving ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="agregar-gasto-titulo"
        className="flex flex-col overflow-hidden w-full"
        style={{
          backgroundColor: '#000',
          borderRadius: '28px',
          border: '1px solid rgba(255, 255, 255, 0.16)',
          maxWidth: '880px',
          maxHeight: 'min(760px, calc(100dvh - 24px))',
          boxShadow: '0 40px 120px rgba(0, 0, 0, 0.8)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-5 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-white/[0.16]">
          <h2
            id="agregar-gasto-titulo"
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '22px',
              lineHeight: '28px',
              letterSpacing: '-0.02em',
              color: 'rgba(255, 255, 255, 0.95)',
              fontFamily: FONT,
            }}
          >
            Agregar gasto
          </h2>
          <p
            className="m-0 mt-1"
            style={{
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.45)',
              fontFamily: FONT,
            }}
          >
            Registra manualmente un gasto para restarlo del balance
          </p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pedidos-scroll px-5 sm:px-6 py-5">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Columna izquierda */}
            <div className="flex-1 flex flex-col gap-5 min-w-0">
              <div className="flex flex-col gap-2">
                <label htmlFor="gasto-descripcion" style={labelStyle}>
                  Descripción *
                </label>
                <input
                  id="gasto-descripcion"
                  type="text"
                  value={form.descripcion}
                  onChange={(e) => handleChange('descripcion', e.target.value)}
                  placeholder="Ej. Compra de harina"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="gasto-categoria" style={labelStyle}>
                  Categoría
                </label>
                <div className="relative">
                  <select
                    id="gasto-categoria"
                    value={form.categoria}
                    onChange={(e) => handleChange('categoria', e.target.value)}
                    className={inputClass}
                    style={selectStyle}
                  >
                    <option value="" style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>
                      Sin categoría
                    </option>
                    {categories.map((c) => (
                      <option
                        key={c.id}
                        value={c.id}
                        style={{ backgroundColor: '#1a1a1a', color: '#fff' }}
                      >
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <SelectChevron />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="gasto-metodo" style={labelStyle}>
                  Método de pago
                </label>
                <div className="relative">
                  <select
                    id="gasto-metodo"
                    value={form.metodo}
                    onChange={(e) => handleChange('metodo', e.target.value)}
                    className={inputClass}
                    style={selectStyle}
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option
                        key={m.value}
                        value={m.value}
                        style={{ backgroundColor: '#1a1a1a', color: '#fff' }}
                      >
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <SelectChevron />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="gasto-notas" style={labelStyle}>
                  Notas
                </label>
                <textarea
                  id="gasto-notas"
                  value={form.notas}
                  onChange={(e) => handleChange('notas', e.target.value)}
                  placeholder="Opcional"
                  rows={3}
                  className={`${inputClass} resize-none`}
                  style={textareaStyle}
                />
              </div>
            </div>

            {/* Columna derecha */}
            <div className="flex-1 flex flex-col gap-5 min-w-0">
              <div className="flex flex-col gap-2">
                <label htmlFor="gasto-monto" style={labelStyle}>
                  Monto (MXN) *
                </label>
                <input
                  id="gasto-monto"
                  type="number"
                  min="0.01"
                  step="0.01"
                  inputMode="decimal"
                  value={form.monto}
                  onChange={(e) => handleChange('monto', e.target.value)}
                  placeholder="0.00"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="gasto-fecha" style={labelStyle}>
                  Fecha del gasto
                </label>
                <input
                  id="gasto-fecha"
                  type="date"
                  value={form.fecha}
                  onChange={(e) => handleChange('fecha', e.target.value)}
                  className={inputClass}
                  style={{ ...inputStyle, colorScheme: 'dark' }}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="gasto-proveedor" style={labelStyle}>
                  Proveedor o proveedora
                </label>
                <input
                  id="gasto-proveedor"
                  type="text"
                  value={form.proveedor}
                  onChange={(e) => handleChange('proveedor', e.target.value)}
                  placeholder="Opcional"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-end gap-2 shrink-0 px-5 sm:px-6 py-4 border-t border-white/[0.16]">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex items-center justify-center h-12 px-6 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-white/[0.85]"
            style={{
              backgroundColor: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: '#000',
              fontFamily: FONT,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.5 : 1,
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="flex items-center justify-center h-12 px-7 rounded-full border-none touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-[#0077ed]"
            style={{
              backgroundColor: canSave ? '#0071e3' : 'rgba(255, 255, 255, 0.06)',
              fontSize: '15px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: canSave ? '#fff' : 'rgba(255, 255, 255, 0.35)',
              fontFamily: FONT,
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
